/**
 * Base interface for file processing progress events.
 * This is the generic interface used when operation type is not known at compile time.
 */
export interface IFileProcessingProgress {
  id: string;
  status: FileProcessingStatus;
  processed?: number;
  total?: number;
  filename?: string;
  error?: string;
  projectId?: string;
  operation?: FileProcessingOperation;
  newFileId?: string;
}

/**
 * Download operation specific data.
 */
export interface IDownloadProgressData {
  phase: 'zipping' | 'downloading';
  currentFile?: string;
  archiveName?: string;
}

/**
 * Translation operation specific data.
 */
export interface ITranslationProgressData {
  sourceLanguage: string;
  targetLanguage: string;
  charactersTranslated?: number;
  estimatedCharacters?: number;
  newFileId?: string;
}

/**
 * PDF export operation specific data.
 */
export interface IPdfExportProgressData {
  conversionStep?: string;
}

/**
 * Operation types.
 */
export type FileProcessingOperation = 'download' | 'export-pdf' | 'translation';

/**
 * Unified status enum.
 */
export enum FileProcessingStatus {
  // Generic statuses
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  PREPARING = 'PREPARING',
  COMPLETED = 'COMPLETED',
  ERROR = 'ERROR',

  // Download specific
  ZIPPING = 'ZIPPING',
  DOWNLOADING = 'DOWNLOADING',

  // Translation specific
  SUBMITTED = 'SUBMITTED',
  ACTIVE = 'ACTIVE',
  FAILED = 'FAILED',
  EXTRACTING_TEXT = 'EXTRACTING_TEXT',
  TRANSLATING_TEXT = 'TRANSLATING_TEXT',
  REPLACING_TEXT = 'REPLACING_TEXT',

  // PDF export specific
  GENERATING_PDF = 'GENERATING_PDF'
}

/**
 * Download progress with operation-specific data.
 */
export interface IDownloadProgress extends IFileProcessingProgress {
  operation: 'download';
  downloadData?: IDownloadProgressData;
}

/**
 * Translation progress with operation-specific data.
 */
export interface ITranslationProgress extends IFileProcessingProgress {
  operation: 'translation';
  translationData?: ITranslationProgressData;
}

/**
 * PDF export progress with operation-specific data.
 */
export interface IPdfExportProgress extends IFileProcessingProgress {
  operation: 'export-pdf';
  pdfExportData?: IPdfExportProgressData;
}

/**
 * Discriminated union of all typed file processing progress events.
 * Use this type when you need type-safe access to operation-specific data.
 */
export type TypedFileProcessingProgress =
  | IDownloadProgress
  | ITranslationProgress
  | IPdfExportProgress
  | IFileProcessingProgress;
